import type { PermissionLookup } from '../iamLookups';
import { shortRoleKey } from '../iamLookups';
import { PermName } from './PermName';
import type { RoleFlowNode } from './roleFlowTree';

// Left-to-right include tree, always expanded; each box shows its own key/title
// and permissions declared (`own`). Bundles render italic + violet, roles plain.

const BOX_WIDTH = 232;

function RoleFlowBox({
    node,
    permissionLookup,
    isRoot
}: {
    node: RoleFlowNode;
    permissionLookup: PermissionLookup;
    isRoot: boolean;
}) {
    const isBundle = node.kind === 'bundle';
    const hasChildren = node.children.length > 0;

    return (
        <div className="flex items-stretch">
            <div
                title={node.key}
                style={{ width: BOX_WIDTH }}
                className={`shrink-0 rounded-lg border p-3 flex flex-col gap-2 ${
                    isBundle ? 'border-violet-200 bg-violet-50/50' : 'border-gray-200 bg-white'
                } ${isRoot ? 'shadow-sm' : ''}`}
            >
                <div className="flex flex-col gap-0.5 min-w-0">
                    <span
                        className={`text-[12px] font-mono font-semibold truncate ${
                            isBundle ? 'italic text-violet-700' : 'text-gray-800'
                        }`}
                    >
                        {shortRoleKey(node.key)}
                    </span>
                    {node.kind === 'role' && (
                        <span className="text-[11px] font-semibold text-gray-600 truncate">
                            {node.title}
                        </span>
                    )}
                </div>
                {node.own.length > 0 && (
                    <div className="flex flex-col gap-1 pt-1.5 border-t border-gray-100">
                        {node.own.map((permissionId) => {
                            const entry = permissionLookup.get(permissionId);
                            return (
                                <PermName
                                    key={permissionId}
                                    permission={permissionId}
                                    group={entry?.group ?? 'unknown'}
                                    wired={entry?.wired ?? false}
                                />
                            );
                        })}
                    </div>
                )}
            </div>

            {hasChildren && (
                <div className="flex flex-col justify-center px-2.5" aria-hidden="true">
                    <div className="w-3 border-t border-gray-300" />
                </div>
            )}

            {hasChildren && (
                <div className="flex flex-col gap-3 justify-center border-l border-gray-200 pl-3">
                    {node.children.map((child) => (
                        <RoleFlowBox
                            key={child.key}
                            node={child}
                            permissionLookup={permissionLookup}
                            isRoot={false}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface RoleFlowProps {
    root: RoleFlowNode;
    permissionLookup: PermissionLookup;
}

// max-h-[420px] bounds the viewport before it scrolls vertically, keeping
// neighboring cards within reach.
export function RoleFlow({ root, permissionLookup }: RoleFlowProps) {
    return (
        <div className="overflow-auto scrollbar-hide -mx-1 px-1 max-h-[420px]">
            <div className="inline-flex py-1">
                <RoleFlowBox node={root} permissionLookup={permissionLookup} isRoot />
            </div>
        </div>
    );
}
